import { Injectable } from "@angular/core";
import { IInsightboard } from "@gtmhub/insightboards";
import { IKpiGroup } from "@gtmhub/kpis";
import { IList } from "@gtmhub/lists";
import { ICollection } from "@gtmhub/models";
import { IPlanningSession } from "@gtmhub/sessions";
import { ITeam } from "@gtmhub/teams";
import { IWhiteboard } from "@gtmhub/whiteboards";
import { INavItem, NavItemsType } from "@webapp/navigation/models/nav-items-list.models";
import { buildNavItemFromModel, getNavItemsType, navItemsFactory, SubNavItemsModel } from "@webapp/navigation/utils/nav-items.util";

import { BehaviorSubject, map, Observable } from "rxjs";
import { ISuggestedModel, SuggestedDtoModel } from "./suggested.models";
import { SuggestedApiService } from "./suggested.api.service";
import { BaseFacade } from "@webapp/core/abstracts/services/base-facade.service";
import { SuggestedState } from "./suggested.state";
import { UXCustomizationType } from "@gtmhub/uxcustomization/models";

@Injectable()
export class SuggestedFacade extends BaseFacade<ISuggestedModel, SuggestedDtoModel, SuggestedState, SuggestedApiService> {
  private suggested$: BehaviorSubject<ISuggestedModel | Record<string, never>> = new BehaviorSubject({});
  private dataLoaded = false;

  constructor(state: SuggestedState, private api: SuggestedApiService) {
    super(state, api);
  }

  public loadSuggested(): void {
    if (this.dataLoaded) return;
    this.api.get$("").subscribe((suggested) => {
      this.suggested$.next(suggested);
      this.dataLoaded = true;
    });
  }

  public getSuggestedSessions$(): Observable<ICollection<INavItem>> {
    return this.getSuggested$("sessions", (entity: IPlanningSession) => navItemsFactory.buildSessionNavItem(entity));
  }

  public getSuggestedLists$(): Observable<ICollection<INavItem>> {
    return this.getSuggested$("lists", (entity: IList) => navItemsFactory.buildListNavItem(entity));
  }

  public getSuggestedDashboards$(): Observable<ICollection<INavItem>> {
    return this.getSuggested$("dashboards", (entity: IInsightboard) => navItemsFactory.buildInsightboardNavItem(entity));
  }

  public getSuggestedWhiteboards$(): Observable<ICollection<INavItem>> {
    return this.getSuggested$("whiteboards", (entity: IWhiteboard) => navItemsFactory.buildWhiteboardNavItem(entity));
  }

  public getSuggestedKpiGroups$(): Observable<ICollection<INavItem>> {
    return this.getSuggested$("kpiGroups", (entity: IKpiGroup) => navItemsFactory.buildKpiGroupNavItem(entity));
  }

  public getSuggestedTeams$(): Observable<ICollection<INavItem>> {
    return this.getSuggested$("teams", (entity: ITeam) => navItemsFactory.buildTeamNavItem(entity));
  }

  private getSuggested$(key: keyof ISuggestedModel, itemBuilderFunc: (item: unknown) => INavItem): Observable<ICollection<INavItem>> {
    return this.suggested$.asObservable().pipe(
      map((suggestedResponse) => {
        const suggestedResponseObj = suggestedResponse[key] || {};
        const items = Object.values(suggestedResponseObj)
          .flat()
          .map((v) => itemBuilderFunc(v));
        const uniqueItems = [...new Map(items.map((m) => [m.id, m])).values()];
        return {
          items: uniqueItems,
          totalCount: uniqueItems.length,
        };
      })
    );
  }

  private getSuggestedType(type: NavItemsType): keyof ISuggestedModel {
    const navItemsTypeToSuggestedTypes: { [key in NavItemsType]?: keyof ISuggestedModel } = {
      sessions: "sessions",
      insightboards: "dashboards",
      people: "teams",
      kpis: "kpiGroups",
      whiteboards: "whiteboards",
      lists: "lists",
    };
    return navItemsTypeToSuggestedTypes[type];
  }

  public removeSuggestedItem(id: string, type: NavItemsType): void {
    const suggestedType: keyof ISuggestedModel = this.getSuggestedType(type);
    if (!suggestedType) return;
    if (!this.suggested$.value[suggestedType]) return;
    const newSuggestionCollection = Object.keys(this.suggested$.value[suggestedType]).reduce((previousValue: Record<string, unknown>, currentValue: string) => {
      previousValue[currentValue] = this.suggested$.value[suggestedType][currentValue].filter((item) => item.id !== id);
      return previousValue;
    }, {});
    this.suggested$.next({ ...this.suggested$.value, [suggestedType]: newSuggestionCollection } as ISuggestedModel);
  }

  public updateItem(uxcType: UXCustomizationType, item: SubNavItemsModel): void {
    const suggestedType: keyof ISuggestedModel = this.getSuggestedType(getNavItemsType(uxcType));
    if (!suggestedType) return;
    if (!this.suggested$.value[suggestedType]) return;
    const newNavItem = buildNavItemFromModel(uxcType, item);
    const newSuggestionCollection = Object.keys(this.suggested$.value[suggestedType]).reduce((previousValue: Record<string, unknown>, currentValue: string) => {
      previousValue[currentValue] = this.suggested$.value[suggestedType][currentValue].map((oldItem) => (oldItem.id === item.id ? item : oldItem));
      return previousValue;
    }, {});
    if (newNavItem !== null) {
      this.suggested$.next({
        ...this.suggested$.value,
        [suggestedType]: newSuggestionCollection,
      } as ISuggestedModel);
    }
  }

  public deleteItem(uxcType: UXCustomizationType, item: SubNavItemsModel): void {
    const suggestedType: keyof ISuggestedModel = this.getSuggestedType(getNavItemsType(uxcType));
    if (!suggestedType) return;
    if (!this.suggested$.value[suggestedType]) return;
    const newNavItem = buildNavItemFromModel(uxcType, item);
    const newSuggestionCollection = Object.keys(this.suggested$.value[suggestedType]).reduce((previousValue: Record<string, unknown>, currentValue: string) => {
      previousValue[currentValue] = this.suggested$.value[suggestedType][currentValue].filter((oldItem) => oldItem.id !== item.id);
      return previousValue;
    }, {});
    if (newNavItem !== null) {
      this.suggested$.next({
        ...this.suggested$.value,
        [suggestedType]: newSuggestionCollection,
      } as ISuggestedModel);
    }
  }
}
